import { Link } from "react-router-dom"
import { gql } from "~/__generated__"
import { BookmarkIndexItemFragment } from "~/__generated__/graphql"
import { useViewerMaybe } from "~/auth/use-viewer"
import { bookmarkDetailPath, bookmarkEditPath, groupDetailPath } from "~/common/paths"
import { useSafeMutation } from "~/common/use-safe-mutation"
import ArrowRight from "~/images/arrow-right.svg?react"
import edit2 from "~/images/edit-2"
import LinkIcon from "~/images/link-icon.svg?react"
import trash2 from "~/images/trash-2"
import { LinkButton } from "~/ui/link-button"
import Pill from "~/ui/pill"
import { RelativeDatetime } from "~/ui/relative-date"
import { toast } from "~/ui/use-toast"
import { UserAvatar } from "~/users/user-avatar"
import { BookmarkEnrichmentScreenshotCardImage } from "./bookmark-enrichment-screenshot-card-image"
import { BookmarkHideButton } from "./bookmark-hide-button"
import { Tag } from "./bookmark-index-screen"
import { BookmarkNotes } from "./bookmark-notes"
import { BookmarkReactions } from "./bookmark-reactions"

const BOOKMARK_DELETE_MUTATION = gql(/* GraphQL */ `
  mutation BookmarkDeleteMutation($input: String!) {
    bookmarkDelete(input: { bookmarkId: $input }) {
      success
    }
  }
`)

gql(/* GraphQL */ `
  fragment BookmarkIndexItem on Bookmark {
    id
    createdAt
    title
    url
    notes
    reactionsCount
    viewerHasReacted
    isHidden
    user {
      id
      firstName
      fullName
      lastName
      ...UserAvatar
    }
    enrichment {
      id
      description
      ...BookmarkEnrichmentScreenshotCardImage
      allTags {
        id
        name
      }
    }
    groups {
      id
      name
    }
  }
`)

type BookmarkIndexItemParams = {
  bookmark: BookmarkIndexItemFragment
  filterTagIds: Array<string>
  toggleFilterTag: (tag: Tag) => void
  refetch: () => Promise<any>
}

export const BookmarkIndexItem: React.FC<BookmarkIndexItemParams> = ({
  bookmark,
  filterTagIds,
  refetch,
  toggleFilterTag,
}) => {
  const { viewer } = useViewerMaybe()

  const [deleteBookmark] = useSafeMutation(BOOKMARK_DELETE_MUTATION, {
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      })
    },
  })

  const handleDeleteBookmark = async () => {
    const confirmResponse = confirm("Are you sure you want to delete this bookmark?")
    if (!confirmResponse) return
    try {
      const deleteResult = await deleteBookmark({ variables: { input: bookmark.id } })
      if (deleteResult.data?.bookmarkDelete?.success) {
        toast({
          title: "Bookmark Deleted",
        })
        refetch()
      } else {
        toast({
          title: "Failed to delete bookmrk",
          description: JSON.stringify(deleteResult.errors),
        })
      }
    } catch (e) {
      console.error("Failed to delete bookmark:", e)
      toast({
        title: "Failed to delete bookmrk",
      })
    }
  }

  return (
    <li className="mt-8 border-t pt-8 first:border-t-0">
      <div className="mb-2 flex text-xs">
        <div className="flex flex-1 space-x-2">
          <div className="">
            <UserAvatar user={bookmark.user} className="h-8 w-8" />
          </div>
          <div className="flex flex-col justify-center align-middle">
            <div>
              {bookmark.groups && bookmark.groups.length > 0 && (
                <span className="mr-1">
                  <span className="mr-1 text-gray-400">In</span>
                  <span>
                    {bookmark.groups.map((group, i) => [
                      i > 0 && ", ",
                      <Link to={groupDetailPath({ id: group.id })} key={group.id}>
                        {group.name}
                      </Link>,
                    ])}
                  </span>
                </span>
              )}
              <span className="text-gray-400">by</span> {bookmark.user?.fullName}
            </div>
          </div>
        </div>
        <div className="relative flex-1 text-right text-gray-400">
          <RelativeDatetime input={bookmark.createdAt} />
          <div className="ml-2 inline-flex rounded border">
            {viewer?.id === bookmark.user.id && (
              <Link
                to={bookmarkEditPath({ bookmarkId: bookmark.id })}
                className="inline-flex h-6 w-6 items-center justify-center hover:bg-gray-100"
                title="Edit bookmark"
              >
                <img src={edit2.src} alt="Edit" width={edit2.width} height={edit2.height} />
              </Link>
            )}
            <BookmarkHideButton bookmark={bookmark} />
            {viewer?.id === bookmark.user.id && (
              <button
                onClick={handleDeleteBookmark}
                className="inline-flex h-6 w-6 items-center justify-center hover:bg-gray-100"
                title="Delete bookmark"
              >
                <img src={trash2.src} alt="Delete" width={trash2.width} height={trash2.height} />
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col md:flex-row md:gap-4">
        <div className="flex flex-1 flex-col">
          <h2 className="truncate text-lg font-medium">
            <Link
              to={bookmarkDetailPath({ bookmarkId: bookmark.id })}
              className="hover:text-blue-500"
            >
              {bookmark.title}
            </Link>
            <a
              href={bookmark.url}
              className="ms-2 text-nowrap break-keep rounded-full bg-brand px-2 py-1 align-top text-[7px] text-white"
              target="_blank"
              rel="noopener noreferrer"
            >
              <LinkIcon className="me-[2px] inline align-text-bottom" />
              View
            </a>
          </h2>
          <p className="mt-2 text-sm">{bookmark.enrichment.description}</p>
          <BookmarkNotes notes={bookmark.notes} userFullName={bookmark.user.fullName} />
          <div className="mt-6 flex flex-wrap justify-start gap-2">
            {bookmark?.enrichment?.allTags &&
              bookmark.enrichment.allTags.length > 0 &&
              bookmark.enrichment.allTags.map((tag) => (
                <Pill
                  key={tag.id}
                  name={tag.name}
                  onClick={() => toggleFilterTag(tag)}
                  className={filterTagIds.includes(tag.id) ? "" : "border bg-white text-gray-800"}
                />
              ))}
          </div>
        </div>
        <div className="mt-4 w-full md:mt-0 md:w-auto">
          <BookmarkEnrichmentScreenshotCardImage enrichment={bookmark.enrichment} />
        </div>
      </div>
      <div className="mt-4 flex items-center gap-2">
        <LinkButton
          to={bookmarkDetailPath({ bookmarkId: bookmark.id })}
          className="flex items-center gap-2 rounded-full"
          size="sm"
        >
          Details
          <ArrowRight />
        </LinkButton>
        <BookmarkReactions
          bookmarkId={bookmark.id}
          reactionsCount={bookmark.reactionsCount}
          viewerHasReacted={bookmark.viewerHasReacted}
          refetch={refetch}
        />
      </div>
    </li>
  )
}
